import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Link,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { apiClient } from '@/lib/api/client'
import { formatWallClockDateTime } from '@/lib/utils/dateTimeUtils'
import { getTicketRequestSourceEmail } from '@/lib/services/ticketRequestService'
import { sanitizeEmailHtml } from '@/lib/utils/emailSafety'
import type { RequestOption } from '@/types/ticket'
import PropertyRow from './PropertyRow'

type Props = {
  isOpen: boolean
  onClose: () => void
  request: RequestOption
  ticketRequestId?: string | null
}

type RequestDetail = Record<string, unknown>

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function parseNumericRequestId(...candidates: Array<string | number | null | undefined>): number | null {
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue
    const n = Number(candidate)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

function requestChannelLabel(request: RequestOption, detail?: RequestDetail | null): string {
  const source = String(detail?.request_source ?? request.requestSource ?? '')
  const channel = String(detail?.request_channel ?? request.requestChannel ?? '')
  return [source, channel].filter(Boolean).join(' · ') || '—'
}

function requestTypeLabel(request: RequestOption, detail?: RequestDetail | null): string {
  const serviceType = String(detail?.service_type ?? request.serviceType ?? '')
  const serviceParent = String(detail?.service_parent ?? request.serviceParent ?? '')
  if (serviceType) {
    return serviceParent ? `${serviceParent} → ${serviceType}` : serviceType
  }
  const categories = (detail?.categories as string[] | undefined) ?? request.categories
  return categories?.join(', ') ?? '—'
}

function originalMessage(request: RequestOption, detail?: RequestDetail | null): string | null {
  const issue = String(detail?.issue_description ?? request.issueDescription ?? '').trim()
  const raw = String(detail?.raw_message ?? '').trim()
  const desc = String(detail?.description ?? request.description ?? '').trim()
  if (issue) return issue
  if (raw) return raw
  if (desc) return desc
  return null
}

function mapAttachments(
  request: RequestOption,
  detail?: RequestDetail | null,
): Array<{ file_name: string; path: string; file_url?: string }> {
  const fromDetail = detail?.attachment_metadata ?? detail?.attachmentMetadata
  if (Array.isArray(fromDetail) && fromDetail.length > 0) {
    return fromDetail as Array<{ file_name: string; path: string; file_url?: string }>
  }
  return request.attachmentMetadata ?? []
}

export default function RequestOriginDrawer({
  isOpen,
  onClose,
  request,
  ticketRequestId,
}: Props) {
  const colors = useThemeColors()
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<RequestDetail | null>(null)
  const [sourceEmailHtml, setSourceEmailHtml] = useState<string | null>(null)
  const [revisionCount, setRevisionCount] = useState(0)

  const numericRequestId = useMemo(
    () => parseNumericRequestId(ticketRequestId, request.id),
    [ticketRequestId, request.id],
  )

  const loadDetail = useCallback(async () => {
    if (!numericRequestId) return
    setLoading(true)
    try {
      const [detailRes, revRes] = await Promise.all([
        apiClient.get(`/support/my-request/${numericRequestId}`),
        apiClient.get(`/support/my-request/${numericRequestId}/revisions`),
      ])
      const data = (detailRes.data?.data ?? detailRes.data) as RequestDetail
      setDetail(data)
      setRevisionCount(((revRes.data?.data ?? revRes.data) as unknown[] | undefined)?.length ?? 0)

      const source = String(data.request_source ?? '')
      const channel = String(data.request_channel ?? '')
      if (source === 'email' || channel === 'email') {
        try {
          const emailRes = await getTicketRequestSourceEmail(numericRequestId)
          setSourceEmailHtml(emailRes?.body_html ?? emailRes?.body ?? null)
        } catch {
          setSourceEmailHtml(null)
        }
      } else {
        setSourceEmailHtml(null)
      }
    } catch {
      setDetail(null)
      setSourceEmailHtml(null)
      setRevisionCount(0)
    } finally {
      setLoading(false)
    }
  }, [numericRequestId])

  useEffect(() => {
    if (isOpen && numericRequestId) {
      void loadDetail()
    }
    if (!isOpen) {
      setDetail(null)
      setSourceEmailHtml(null)
      setRevisionCount(0)
    }
  }, [isOpen, numericRequestId, loadDetail])

  const submittedAt =
    (detail?.submitted_at as string | undefined) ??
    request.submittedAt ??
    request.requestedAt ??
    request.createdAt
  const message = originalMessage(request, detail)
  const enrichedDesc = String(detail?.description ?? request.description ?? '').trim()
  const showEnriched = Boolean(enrichedDesc) && enrichedDesc !== request.issueDescription?.trim()
  const attachments = mapAttachments(request, detail)
  const preferredSchedule =
    (detail?.preferred_schedule_at as string | undefined) ?? request.preferredScheduleAt
  const serialNumber = String(detail?.serial_number ?? request.serialNumber ?? '').trim()
  const unitModel = String(detail?.unit_model ?? request.unitModel ?? '').trim()
  const categories =
    (detail?.categories as string[] | undefined)?.filter(Boolean) ?? request.categories ?? []
  const requestNumber = String(detail?.request_number ?? request.id ?? '')
  const confidenceScore = detail?.confidence_score

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
      <DrawerOverlay />
      <DrawerContent bg={colors.cardBg}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={colors.borderLight} pb={3}>
          <Text fontSize="md" fontWeight="600" color={colors.text}>
            {requestNumber}
          </Text>
          <Text fontSize="xs" color={colors.textSecondary} fontWeight="normal" mt={0.5}>
            {requestChannelLabel(request, detail)}
            {submittedAt ? ` · ${formatDate(submittedAt)}` : ''}
          </Text>
        </DrawerHeader>

        <DrawerBody py={4}>
          {loading ? (
            <HStack justify="center" py={10}>
              <Spinner size="sm" />
            </HStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Badge
                alignSelf="flex-start"
                colorScheme="green"
                fontSize="0.65rem"
                px={2}
                py={0.5}
                borderRadius="md"
                textTransform="none"
              >
                Converted to this ticket
              </Badge>

              <Box>
                <PropertyRow
                  label="Contact"
                  value={String(detail?.contact_name ?? request.contactName ?? '—')}
                />
                <PropertyRow
                  label="Email"
                  value={String(detail?.contact_email ?? request.contactEmail ?? '—')}
                />
                <PropertyRow
                  label="Client"
                  value={String(
                    detail?.client_org ?? detail?.client_name ?? request.clientOrg ?? request.clientName ?? '—',
                  )}
                />
                <PropertyRow label="Request type" value={requestTypeLabel(request, detail)} />
                {preferredSchedule ? (
                  <PropertyRow label="Preferred schedule" value={formatWallClockDateTime(preferredSchedule)} />
                ) : null}
                {serialNumber ? <PropertyRow label="Serial number" value={serialNumber} /> : null}
                {unitModel ? <PropertyRow label="Unit model" value={unitModel} /> : null}
              </Box>

              {categories.length > 0 ? (
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>
                    Categories
                  </Text>
                  <Wrap spacing={1.5}>
                    {categories.map((cat) => (
                      <WrapItem key={cat}>
                        <Badge colorScheme="gray" fontSize="0.65rem" borderRadius="md" textTransform="none">
                          {cat}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              ) : null}

              {sourceEmailHtml ? (
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>
                    Original email
                  </Text>
                  <Box
                    p={3}
                    borderWidth="1px"
                    borderColor={colors.border}
                    borderRadius="md"
                    bg={colors.sectionBg ?? 'gray.50'}
                    maxH="240px"
                    overflowY="auto"
                    fontSize="sm"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(sourceEmailHtml) }}
                  />
                </Box>
              ) : message ? (
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>
                    Original message
                  </Text>
                  <Box
                    p={3}
                    borderWidth="1px"
                    borderColor={colors.border}
                    borderRadius="md"
                    bg={colors.sectionBg ?? 'gray.50'}
                  >
                    <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.6">
                      {message}
                    </Text>
                  </Box>
                </Box>
              ) : null}

              {showEnriched && enrichedDesc ? (
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>
                    Enriched request
                  </Text>
                  <Text fontSize="sm" color={colors.textSecondary} whiteSpace="pre-wrap" lineHeight="1.6">
                    {enrichedDesc}
                  </Text>
                </Box>
              ) : null}

              {attachments.length > 0 ? (
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>
                    Attachments ({attachments.length})
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {attachments.map((file) => {
                      const href = file.file_url ?? file.path
                      return (
                        <Link
                          key={`${file.file_name}-${file.path}`}
                          href={href}
                          isExternal
                          fontSize="sm"
                          color={colors.primary}
                        >
                          {file.file_name}
                        </Link>
                      )
                    })}
                  </VStack>
                </Box>
              ) : null}

              <Box>
                <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1.5}>
                  Activity
                </Text>
                <VStack align="stretch" spacing={1}>
                  {request.acknowledgedAt || detail?.acknowledged_at ? (
                    <Text fontSize="xs" color={colors.textSecondary}>
                      Acknowledged {formatDate(String(request.acknowledgedAt ?? detail?.acknowledged_at ?? ''))}
                    </Text>
                  ) : null}
                  {submittedAt ? (
                    <Text fontSize="xs" color={colors.textSecondary}>
                      Submitted {formatDate(submittedAt)}
                    </Text>
                  ) : null}
                  {revisionCount > 0 ? (
                    <Text fontSize="xs" color={colors.textSecondary}>
                      {revisionCount} revision{revisionCount === 1 ? '' : 's'}
                    </Text>
                  ) : null}
                  {confidenceScore != null ? (
                    <Text fontSize="xs" color={colors.textSecondary}>
                      AI confidence: {String(confidenceScore)}%
                    </Text>
                  ) : null}
                  {!request.acknowledgedAt && !detail?.acknowledged_at && !submittedAt && revisionCount === 0 ? (
                    <Text fontSize="xs" color={colors.textMuted}>
                      No activity recorded
                    </Text>
                  ) : null}
                </VStack>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
